import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import { wapi } from "@/lib/api";
import { MuralBoard } from "@/components/workers/MuralBoard";
import { StudiesBoard } from "@/components/workers/StudiesBoard";
import { TeamAdmin } from "@/components/workers/TeamAdmin";
import { enablePush, disablePush, hasActivePush, pushSupport } from "@/lib/push";

const LoginView = ({ denied }) => {
  const [pwEmail, setPwEmail] = useState("");
  const [pwPass, setPwPass] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState("");

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/trabalhadores";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginPassword = async () => {
    if (!pwEmail.trim() || !pwPass) {
      setPwError("Informe o e-mail autorizado e a senha.");
      return;
    }
    setPwBusy(true);
    setPwError("");
    try {
      const r = await wapi.post("/auth/login", { email: pwEmail.trim(), password: pwPass });
      localStorage.setItem("workers_token", r.data.session_token);
      window.location.reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setPwError(typeof d === "string" ? d : "Não foi possível entrar.");
      setPwBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-background text-foreground grid place-items-center px-6 py-16">
      <div className="w-full max-w-lg text-center" data-testid="workers-login">
        <img src="/estesia-emblem.png" alt="Estesia" className="h-16 w-16 mx-auto rounded-full bg-white shadow-md ring-1 ring-black/5 object-contain" />
        <h1 className="mt-6 font-serif text-3xl md:text-4xl">Área dos Trabalhadores</h1>
        <p className="mt-4 font-sans text-base md:text-lg font-light leading-relaxed text-muted-foreground">
          Espaço privado da equipe do Estesia: recados, organização da sede e escala dos estudos — tudo num lugar só, sem se perder no WhatsApp.
        </p>
        {denied && (
          <p className="mt-6 font-sans text-base text-amber-700 dark:text-amber-300 leading-relaxed" data-testid="login-denied">
            {denied}
          </p>
        )}
        <button
          onClick={login}
          data-testid="google-login-btn"
          className="mt-8 inline-flex items-center gap-3 rounded-full bg-primary px-9 py-4 font-sans text-base font-semibold text-primary-foreground shadow-md shadow-primary/25 hover:bg-primary/90 hover:-translate-y-0.5 transition-all duration-300"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81Z"/></svg>
          Entrar com Google
        </button>
        <p className="mt-6 font-sans text-sm text-muted-foreground">
          O acesso é liberado somente para e-mails autorizados pela equipe.
        </p>

        <div className="mt-8 border-t border-border pt-6" data-testid="password-login">
          <p className="font-sans text-xs uppercase tracking-[0.15em] text-muted-foreground">
            Sem conta Google? Entre com e-mail e senha
          </p>
          <div className="mt-3 space-y-2">
            <input
              type="email" value={pwEmail} onChange={(e) => setPwEmail(e.target.value)}
              placeholder="Seu e-mail autorizado" autoComplete="username"
              className="w-full min-h-[48px] px-4 bg-secondary/40 rounded-md border border-border text-base outline-none focus:border-primary transition-colors"
              data-testid="password-login-email"
            />
            <input
              type="password" value={pwPass} onChange={(e) => setPwPass(e.target.value)}
              placeholder="Senha combinada com a equipe" autoComplete="current-password"
              onKeyDown={(e) => e.key === "Enter" && loginPassword()}
              className="w-full min-h-[48px] px-4 bg-secondary/40 rounded-md border border-border text-base outline-none focus:border-primary transition-colors"
              data-testid="password-login-pass"
            />
            {pwError && <p className="font-sans text-sm text-red-600" data-testid="password-login-error">{pwError}</p>}
            <button
              onClick={loginPassword} disabled={pwBusy}
              className="w-full min-h-[48px] rounded-full border border-primary/40 px-6 py-3 font-sans text-sm font-semibold text-foreground hover:bg-primary/10 hover:border-primary transition-colors disabled:opacity-50"
              data-testid="password-login-btn"
            >
              {pwBusy ? "Entrando…" : "Entrar com senha"}
            </button>
          </div>
        </div>
        <Link to="/" className="mt-8 inline-block font-sans text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="back-to-site">
          ← Voltar ao site
        </Link>
      </div>
    </div>
  );
};

const PushCard = () => {
  const [support, setSupport] = useState("unsupported");
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSupport(pushSupport());
    hasActivePush().then(setActive);
  }, []);

  if (support === "unsupported") return null;

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      setActive(true);
      toast.success("Notificações ativadas neste aparelho.");
    } catch (e) {
      if (e.message === "denied") {
        setSupport("denied");
      } else if (e.message !== "dismissed") {
        toast.error("Não foi possível ativar as notificações.");
      }
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    await disablePush();
    setActive(false);
    setBusy(false);
    toast.success("Notificações desativadas neste aparelho.");
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      await wapi.post("/workers/push/test");
      toast.success("Teste enviado — veja se a notificação chegou neste aparelho.");
    } catch {
      toast.error("Não foi possível enviar o teste.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 flex items-start gap-4" data-testid="push-card">
      <span className="mt-0.5 text-primary"><Bell size={20} strokeWidth={1.5} /></span>
      <div className="flex-1">
        <p className="font-sans text-base font-medium text-foreground">Notificações no celular</p>
        {support === "ios-install" ? (
          <p className="mt-1 font-sans text-sm text-muted-foreground leading-relaxed" data-testid="push-ios-hint">
            No iPhone/iPad: abra esta página no Safari, toque em <strong>Compartilhar</strong> → <strong>Adicionar à Tela de Início</strong> e abra pelo ícone do Estesia. Aí é possível ativar as notificações.
          </p>
        ) : support === "denied" ? (
          <p className="mt-1 font-sans text-sm text-muted-foreground leading-relaxed" data-testid="push-denied">
            As notificações estão bloqueadas neste navegador. Libere nas configurações do site e recarregue a página.
          </p>
        ) : (
          <p className="mt-1 font-sans text-sm text-muted-foreground leading-relaxed" data-testid="push-status">
            {active
              ? "Este aparelho recebe avisos de novos recados e mudanças na escala."
              : "Receba um aviso aqui no aparelho quando houver recado novo no mural ou mudança na escala."}
          </p>
        )}
        {support === "ok" && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
              onClick={active ? disable : enable}
              disabled={busy}
              data-testid="push-toggle-btn"
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-sans text-sm font-semibold transition-colors ${
                active
                  ? "border border-border text-muted-foreground hover:text-foreground"
                  : "bg-primary text-primary-foreground hover:bg-primary/90"
              }`}
            >
              {active ? <BellOff size={15} strokeWidth={1.5} /> : <BellRing size={15} strokeWidth={1.5} />}
              {busy ? "Aguarde…" : active ? "Desativar neste aparelho" : "Ativar neste aparelho"}
            </button>
            {active && (
              <button
                onClick={sendTest}
                disabled={busy}
                data-testid="push-test-btn"
                className="rounded-full border border-border px-4 py-2 font-sans text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Enviar teste
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function WorkersArea() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [denied, setDenied] = useState("");

  useEffect(() => {
    document.title = "Área dos Trabalhadores — Estesia Bern";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  useEffect(() => {
    if (location.state?.user) return; // usuário veio do callback do Google
    wapi.get("/auth/me")
      .then((r) => setUser(r.data.user))
      .catch((e) => {
        if (e?.response?.status === 403) setDenied(e.response.data?.detail || "");
        setUser(false);
      })
      .finally(() => setChecking(false));
  }, [location.state]);

  const logout = async () => {
    try { await disablePush(); } catch {}
    try { await wapi.post("/auth/logout"); } catch {}
    localStorage.removeItem("workers_token");
    toast.success("Você saiu da área dos trabalhadores.");
    navigate("/trabalhadores", { replace: true });
    setUser(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-background grid place-items-center">
        <p className="font-sans text-muted-foreground">Carregando…</p>
      </div>
    );
  }
  if (!user) return <LoginView denied={denied} />;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="workers-area">
      <header className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-40">
        <div className="mx-auto max-w-5xl px-5 md:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="workers-logo">
            <img src="/estesia-emblem.png" alt="Estesia" className="h-9 w-9 rounded-full bg-white ring-1 ring-black/5 object-contain" />
            <span className="font-serif text-lg md:text-xl">Área dos Trabalhadores</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block font-sans text-sm text-muted-foreground" data-testid="workers-user">
              Olá, {user.name || user.email}
            </span>
            <button
              onClick={logout}
              data-testid="workers-logout"
              className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 font-sans text-sm text-muted-foreground hover:text-primary hover:border-primary transition-colors"
            >
              <LogOut size={15} strokeWidth={1.5} /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 md:px-8 py-8 md:py-12 space-y-16">
        <PushCard />
        <StudiesBoard user={user} />
        <MuralBoard user={user} />
        {user.is_admin && <TeamAdmin />}
      </main>
    </div>
  );
}
